import React, { useState, useEffect } from "react";
import { Grid, GridColumn, getSelectedState } from "@progress/kendo-react-grid";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { getter } from "@progress/kendo-react-common";

import { useSelector } from "react-redux";
import useReloadButton from "../../../../../CustomHooks/useReloadButton";
import { useKendoGridHandlers } from "../../../../../CustomHooks/useKendoGridHandlers";
import { FetchData } from "../../../../../utils/array";
import { DisplayMsg } from "../../../../../services/toastService";

const DATA_ITEM_KEY = "id";
const SELECTED_FIELD = "selected";
const idGetter = getter(DATA_ITEM_KEY);

export const DEFAULT_GRID_INFO_CLIENT_PATIENT_FORM = {
  action: "LOADPATIENTFORM",
  className: "PRACTICEDETAIL",
  pageSize: 20,
  currentPageNumber: 1,
  sortName: "TITLE",
  sortOrder: "desc",
  gridHeight: "300px",
};

const PAGER_SETTINGS = {
  info: true,
  type: "input",
  previousNext: true,
  pageSize: 4,
  refresh: true,
};

const KendoPatientFormGrid = React.memo((props) => {
  const { entityId } = props;
  const sharedData = useSelector((state) => state.SharedLayoutData);
  const [gridData, setGridData] = useState([]);
  const [total, setTotal] = useState(0);
  const [reloadToggle, setReloadToggle] = useState(false);

  const [gridState, setGridState] = useState({
    pageNumber: DEFAULT_GRID_INFO_CLIENT_PATIENT_FORM.currentPageNumber,
    pageSize: DEFAULT_GRID_INFO_CLIENT_PATIENT_FORM.pageSize,
    sortName: DEFAULT_GRID_INFO_CLIENT_PATIENT_FORM.sortName,
    sortOrder: DEFAULT_GRID_INFO_CLIENT_PATIENT_FORM.sortOrder,
    sort: [
      {
        field: DEFAULT_GRID_INFO_CLIENT_PATIENT_FORM.sortName,
        dir:
          DEFAULT_GRID_INFO_CLIENT_PATIENT_FORM.sortOrder?.toLowerCase() ||
          "asc",
      },
    ],
  });

  const { HandleDataStateChange, HandlePageChange } =
    useKendoGridHandlers(setGridState);

  const [selectedState, setSelectedState] = React.useState();

  // UseEffect to load the grid data
  useEffect(() => {
    LoadClientPatientFormGridData();
  }, [gridState, reloadToggle, props]);

  const LoadClientPatientFormGridData = async () => {
    document.getElementById("loader").style.display = "block";

    const queryParams = new URLSearchParams({
      facilityId:
        sharedData.currentFacilityId === 0 ? "" : sharedData.currentFacilityId,
    }).toString();

    const response = await FetchData(
      `Practice/${sharedData.currentEntityId}/patientForm`,
      "PEHR",
      queryParams,
      sharedData.clientApiKey
    );

    if (response && response.succeeded) {
      setGridData(response.data.rows);
      setTotal(response.data.total);
    } else {
      DisplayMsg("2", response.Message);
      setGridData([]);
      setTotal(0);
    }

    document.getElementById("loader").style.display = "none";
  };

  useReloadButton(setReloadToggle);

  const SelectedContentLength = () => {
    const selectedText = window.getSelection().toString();
    return selectedText.length > 0;
  };

  const onSelectionChange = React.useCallback(
    (event) => {
      const check = SelectedContentLength();
      if (!check) {
        const newSelectedState = getSelectedState({
          event,
          selectedState,
          dataItemKey: DATA_ITEM_KEY,
        });
        setSelectedState(newSelectedState);
      }
    },
    [selectedState]
  );

  const ShowPatientDetail = (data) => {
    props.ParentCallBack({
      ...props.State,
      formId: data.id,
      formName: data.title,
      jotUrl: (data.filePath =
        data.filePath != null && data.filePath.includes("jotform.com")
          ? data.filePath
          : `https://form.jotform.com${data.filePath}`),
      jotField: data.jotFields,
      jotFormId: data.jotFormId,
      update: true,
    });
  };

  const Patientname = (props) => (
    <td>
      <span
        role="button"
        className="jotForm"
        title={`select to view detail of ${props.dataItem.title}`}
        onClick={() => ShowPatientDetail(props.dataItem)}
      >
        {props.dataItem.title}
      </span>
    </td>
  );

  return (
    <div>
      <Tooltip openDelay={100} position="right" anchorElement="target">
        <Grid
          style={{ height: "calc(100vh - 417px)" }}
          scrollable="scrollable"
          total={total}
          skip={(gridState.pageNumber - 1) * gridState.pageSize}
          pageSize={gridState.pageSize}
          filterable={false}
          sortable={{
            mode: "single",
            allowUnsort: false,
          }}
          sort={gridState.sort}
          selectedField={SELECTED_FIELD}
          data={gridData.map((item) => ({
            ...item,
            [SELECTED_FIELD]: selectedState?.[idGetter(item)] || false,
          }))}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: "multiple",
          }}
          pageable={PAGER_SETTINGS}
          onPageChange={HandlePageChange}
          onDataStateChange={HandleDataStateChange}
          onSelectionChange={onSelectionChange}
        >
          <GridColumn
            title="Form Name"
            field="title"
            cell={Patientname}
            width={240}
            sortable
          />
          <GridColumn title="Jot Url" field="filePath" width={240} />
          <GridColumn title="Jot Form Id" field="jotFormId" width={242} />
          <GridColumn title="Jot Fields" field="jotFields" width={241} />
        </Grid>
      </Tooltip>
    </div>
  );
});
export default KendoPatientFormGrid;
